/**
 * Color palettes for pixel art sprites.
 *
 * Palette indices used across all sprite definitions:
 *   0 = transparent
 *   1 = primary body color (backend-specific)
 *   2 = secondary body color (darker shade)
 *   3 = skin tone
 *   4 = skin shadow
 *   5 = hair color
 *   6 = outline / dark
 *   7 = white / highlight
 *   8 = monitor glow (backend-specific, brighter)
 *   9 = desk surface
 *  10 = desk shadow
 *  11 = desk highlight
 *  12 = chair color
 *  13 = floor accent
 *  14 = metal / grey
 *  15 = error red
 */

import type { Palette } from "../SpriteSheet.js";

// ---- RGBA helpers ----

type RGBA = [number, number, number, number];

function rgba(r: number, g: number, b: number, a = 255): RGBA {
  return [r, g, b, a];
}

// ---- Common colors shared across palettes ----

const SKIN = rgba(235, 195, 154);
const SKIN_SHADOW = rgba(198, 157, 118);
const HAIR_DARK = rgba(60, 40, 30);
const OUTLINE = rgba(30, 25, 35);
const WHITE = rgba(240, 240, 245);
const DESK_SURFACE = rgba(80, 65, 55);
const DESK_SHADOW = rgba(55, 45, 38);
const DESK_HIGHLIGHT = rgba(105, 85, 72);
const CHAIR_GREY = rgba(70, 65, 75);
const FLOOR_ACCENT = rgba(45, 40, 50);
const METAL = rgba(120, 118, 125);
const ERROR_RED = rgba(220, 50, 50);

// ---- Backend-specific palettes ----

function buildPalette(primary: RGBA, secondary: RGBA, glow: RGBA): Palette {
  const p: Palette = new Map();
  // 0 = transparent (not in map)
  p.set(1, primary);
  p.set(2, secondary);
  p.set(3, SKIN);
  p.set(4, SKIN_SHADOW);
  p.set(5, HAIR_DARK);
  p.set(6, OUTLINE);
  p.set(7, WHITE);
  p.set(8, glow);
  p.set(9, DESK_SURFACE);
  p.set(10, DESK_SHADOW);
  p.set(11, DESK_HIGHLIGHT);
  p.set(12, CHAIR_GREY);
  p.set(13, FLOOR_ACCENT);
  p.set(14, METAL);
  p.set(15, ERROR_RED);
  return p;
}

/** Claude (purple) */
export const PALETTE_CLAUDE = buildPalette(
  rgba(128, 0, 255),   // brand purple
  rgba(90, 0, 180),    // darker purple
  rgba(170, 80, 255),  // glow
);

/** OpenAI (green) */
export const PALETTE_OPENAI = buildPalette(
  rgba(0, 220, 100),   // brand green
  rgba(0, 160, 70),    // darker green
  rgba(80, 255, 150),  // glow
);

/** Cloudflare (pink) */
export const PALETTE_CLOUDFLARE = buildPalette(
  rgba(255, 0, 128),   // brand pink
  rgba(180, 0, 90),    // darker pink
  rgba(255, 100, 170), // glow
);

/** Raw API / default (cyan) */
export const PALETTE_CYAN = buildPalette(
  rgba(0, 200, 255),   // brand cyan
  rgba(0, 140, 180),   // darker cyan
  rgba(100, 220, 255), // glow
);

/** Map a backend string to its palette. */
export function getPaletteForBackend(backend: string): Palette {
  switch (backend) {
    case "claude-agent-sdk":
      return PALETTE_CLAUDE;
    case "openai-agents":
      return PALETTE_OPENAI;
    case "cloudflare-agents":
      return PALETTE_CLOUDFLARE;
    default:
      return PALETTE_CYAN;
  }
}

// ---- Furniture / tile palettes (non-character) ----

/** Generic furniture palette. Uses indices 9-14 from base + extras. */
export const FURNITURE_PALETTE: Palette = new Map([
  [1, DESK_SURFACE],
  [2, DESK_SHADOW],
  [3, DESK_HIGHLIGHT],
  [4, OUTLINE],
  [5, METAL],
  [6, CHAIR_GREY],
  [7, WHITE],
  [8, rgba(60, 180, 80)],  // plant green
  [9, rgba(40, 120, 50)],  // plant dark
  [10, rgba(140, 80, 50)], // pot brown
  [11, rgba(100, 60, 35)], // pot shadow
  [12, rgba(50, 45, 55)],  // dark surface
  [13, rgba(90, 85, 95)],  // light surface
  [14, FLOOR_ACCENT],
]);

/** Office floor tile palette. */
export const TILE_PALETTE: Palette = new Map([
  [1, rgba(158, 148, 136)],  // base office floor (warm beige — slightly darker)
  [2, rgba(188, 180, 168)],  // office floor lighter (+30 units for visible checkerboard)
  [3, rgba(145, 135, 125)],  // office floor darker
  [4, rgba(185, 175, 160)],  // floor highlight edge
  [5, rgba(120, 112, 104)],  // floor shadow edge (darker)
  [6, rgba(140, 145, 150)],  // factory concrete
  [7, rgba(155, 158, 162)],  // factory lighter
  [8, rgba(125, 128, 132)],  // factory darker
  [9, rgba(170, 130, 95)],   // stage wood
  [10, rgba(185, 145, 108)], // stage wood lighter
  [11, rgba(150, 115, 82)],  // stage wood darker
  [12, rgba(60, 55, 65, 80)], // shadow tile overlay (subtle, semi-transparent)
  [13, rgba(115, 100, 88)],  // manager floor — dark warm wood
  [14, rgba(135, 120, 105)], // manager floor alt
  [15, rgba(148, 155, 150)], // break floor — cool grey-green linoleum
  [16, rgba(165, 172, 166)], // break floor alt
  [17, rgba(172, 168, 162)], // corridor floor — warm neutral stone
  [18, rgba(185, 181, 176)], // corridor floor alt
  [19, rgba(85, 140, 60)],   // grass green base
  [20, rgba(100, 155, 70)],  // grass green lighter
  [21, rgba(115, 165, 80)],  // grass edge light
  [22, rgba(60, 110, 45)],   // grass edge dark
  [23, rgba(155, 150, 140)], // path stone base
  [24, rgba(170, 165, 155)], // path stone lighter
  [25, rgba(145, 140, 130)], // path stone edge light
  [26, rgba(125, 120, 112)], // path stone edge dark
]);

/** Wall palette — light neutral walls (Theme Hospital style). */
export const WALL_PALETTE: Palette = new Map([
  [1, rgba(195, 190, 182)],  // wall face NW — warm off-white
  [2, rgba(215, 210, 200)],  // wall top / cap — lighter cream
  [3, rgba(155, 150, 142)],  // wall shadow — muted warm grey
  [4, rgba(225, 220, 212)],  // wall edge highlight — bright cream
  [5, rgba(170, 165, 158)],  // wall face NE — slightly darker (shadow side)
  [6, rgba(140, 135, 128)],  // wall NE shadow — medium warm grey
  [7, rgba(110, 105, 98)],   // wall base shadow — ground shadow
]);

// ---- Effect palettes ----

export const EFFECT_PALETTE: Palette = new Map([
  [1, rgba(255, 255, 255)],       // white
  [2, rgba(200, 200, 210)],       // light grey
  [3, rgba(150, 150, 160)],       // mid grey
  [4, rgba(128, 0, 255)],         // purple (delegates-to)
  [5, rgba(0, 200, 255)],         // cyan (reports-to)
  [6, rgba(0, 220, 100)],         // green (peers-with)
  [7, rgba(255, 0, 128)],         // pink (pipes-to)
  [8, rgba(220, 50, 50)],         // error red
  [9, rgba(255, 200, 50)],        // warning yellow
  [10, rgba(180, 180, 190, 180)], // smoke (semi-transparent)
  [11, rgba(140, 140, 150, 120)], // smoke darker
  [12, rgba(255, 240, 100)],      // spark yellow
  [13, rgba(255, 180, 50)],       // spark orange
]);
